import * as THREE from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { OrbitControls } from './controls/OrbitControls';
import { CSS2DRenderer } from './renderer/CSS2DRenderer';
import TWEEN from '@tweenjs/tween.js';
import { cameraPositionZToZoom, isElement, zoomToCameraPositionZ } from '../utils';
import InteractControls from './controls/InteractControls';
import Objects from './element';
import store from './store';
var MapUpdateType;
(function (MapUpdateType) {
    MapUpdateType[MapUpdateType["element"] = 0] = "element";
    MapUpdateType[MapUpdateType["scene"] = 1] = "scene";
})(MapUpdateType || (MapUpdateType = {}));
class BaseMap extends THREE.EventDispatcher {
    intersects; // 光线投影控制器（获取当前对象）
    renderer; // WebGLRenderer
    _labelRender; // CSS2dRender
    container; // 宿主DOM
    stats; // FPS检测器
    _elements = []; // 数据源
    controls; // 轨道控制器
    scene; // 场景
    animate; // 动画
    animateId;
    // private _elementMap: Map<string, string>; // 所有元素存储
    _elementMap; // 所有元素存储
    _group; // 初始化分组
    _toolGroup; // 初始化工具分组
    _asyncTag; // 是否是异步标志
    _tempList; // 暂存数据变化数组
    debug; // debug模式
    defaultZoom;
    minZoom;
    maxZoom;
    delayTimer;
    _collectElements = new Map(); // 数据源
    _backgroundColor = '';
    _onload;
    update;
    destory;
    onClick;
    onMouseMove;
    onMouseEnter;
    onMouseLeave;
    onMouseDown;
    onMouseUp;
    camera; // 相机
    // public elements: any[];
    constructor(data) {
        super();
        const { container, enableRotate = true, debug = false, elements, zoom, minZoom, maxZoom, mouseLeft = 'PAN', mouseMiddle = 'DOLLY', mouseRight = 'ROTATE', backgroundColor = '#fff', mapMode = 'normal', } = data;
        // Error
        if (!container || !isElement(container))
            throw new TypeError('canvas类型有误');
        // Init
        this.container = container;
        this._elementMap = new Map();
        this._onload = null;
        this.update = null;
        this.onClick = undefined;
        this.onMouseMove = undefined;
        this.onMouseEnter = undefined;
        this.onMouseLeave = undefined;
        this.onMouseDown = undefined;
        this.onMouseUp = undefined;
        this._asyncTag = false;
        this._tempList = [];
        this.debug = debug;
        this.defaultZoom = zoom;
        this.minZoom = minZoom;
        this.maxZoom = maxZoom;
        this.animateId = -1;
        this.delayTimer = 0;
        // Scene
        this.scene = new THREE.Scene();
        this.mapBackgroundColor = backgroundColor;
        store.scene = this.scene;
        // Sizes
        const sizes = {
            width: this.container.clientWidth,
            height: this.container.clientHeight,
        };
        // Camera
        this.camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 10000);
        store.camera = this.camera;
        this.camera.updateProjectionMatrix();
        const cameraDefaultZoom = zoomToCameraPositionZ(zoom); // 镜头默认高度
        this.camera.position.set(0, 0, cameraDefaultZoom);
        this.scene.add(this.camera);
        // add listener
        this.EventHandler = this.EventHandler.bind(this);
        this._resize = this._resize.bind(this);
        this.destory = this.dispose.bind(this);
        window.addEventListener('resize', this._resize, false);
        // Renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, depth: true });
        this.renderer.setSize(sizes.width, sizes.height);
        store.renderer = this.renderer;
        // Group
        this._group = new THREE.Group();
        this.scene.add(this._group);
        // tool group
        this._toolGroup = new THREE.Group();
        this.scene.add(this._toolGroup);
        store.toolGroup = this._toolGroup;
        // Add Elements
        // this._handleElementChange();
        this.elements = elements;
        // CSS2DRenderer
        this._labelRender = new CSS2DRenderer();
        this._labelRender.setSize(sizes.width, sizes.height);
        this._labelRender.domElement.style.position = 'absolute';
        this._labelRender.domElement.style.top = '0px';
        // domRender
        this.container.appendChild(this.renderer.domElement);
        this.container.appendChild(this._labelRender.domElement);
        // OrbitControls
        this.controls = new OrbitControls(this.camera, this.container);
        store.controls = this.controls;
        this.controls.mouseButtons = {
            LEFT: THREE.MOUSE[mouseLeft],
            MIDDLE: THREE.MOUSE[mouseMiddle],
            RIGHT: THREE.MOUSE[mouseRight],
        };
        this.controls.enableRotate = false;
        // this.controls.enableRotate = [mouseLeft, mouseMiddle, mouseRight].includes('ROTATE');
        const cameraPositionMinZ = zoomToCameraPositionZ(maxZoom);
        const cameraPositionMaxZ = zoomToCameraPositionZ(minZoom);
        this.controls.minDistance = cameraPositionMinZ;
        this.controls.maxDistance = cameraPositionMaxZ;
        // function generateSprite() {
        //   const canvas = document.createElement('canvas');
        //   canvas.width = width;
        //   canvas.height = height;
        //   const context = canvas.getContext('2d');
        //   if (context) {
        //     context.beginPath();
        //     context.arc(radius, radius, radius, 0, 2 * Math.PI);
        //     context.closePath();
        //     // 创建渐变色: r,g,b取值比较自由，我们只关注alpha的数值
        //     const radialGradient = context.createRadialGradient(radius, radius, 0, radius, radius, radius);
        //     // 添加一个由偏移值和颜色值指定的断点到渐变
        //     // radialGradient.addColorStop(1.0, "#FF000000");
        //     colorStops.forEach(({ offset, color }) => {
        //       radialGradient.addColorStop(offset, color);
        //     });
        //     context.fillStyle = radialGradient;
        //     const { max, min } = this.getMaxMin(size);
        //     // 设置globalAlpha: 需注意取值需规范在0-1之间
        //     const globalAlpha = (size - min) / (max - min);
        //     context.globalAlpha = Math.max(Math.min(globalAlpha, 1), 0);
        //     // 填充颜色
        //     context.fill();
        //   }
        //   const canvasTexture = new THREE.CanvasTexture(canvas);
        //   canvasTexture.needsUpdate = true;
        //   return canvasTexture;
        // }
        // const geom = new THREE.CircleGeometry(50, 32);
        // const material = new THREE.MeshBasicMaterial({
        //   color: 0xff0000,
        //   transparent: true,
        //   // map: generateSprite(),
        //   depthTest: false
        // });
        // const cloud = new THREE.Points(geom, material);
        // cloud.position.set(-50, -50, 10);
        // this.scene.add(cloud);
        // InteractControls  监听所有 element
        this.intersects = new InteractControls(this.camera, this.container, this.scene, false);
        this.intersects.addEventListener('mousedown', this.EventHandler);
        this.intersects.addEventListener('mouseup', this.EventHandler);
        this.intersects.addEventListener('mousemove', this.EventHandler);
        this.intersects.addEventListener('click', this.EventHandler);
        this.intersects.addEventListener('mouseenter', this.EventHandler);
        this.intersects.addEventListener('mouseleave', this.EventHandler);
        // Debug
        if (debug) {
            // 添加坐标轴
            const axesHelper = new THREE.AxesHelper(5000);
            // axesHelper.position.z = -1;
            this.scene.add(axesHelper);
            // 添加网格
            const gridHelper = new THREE.GridHelper(10000, 30, 0x2c2c2c, 0x888888);
            this.scene.add(gridHelper);
            // 添加FPS检测
            this.stats = new Stats();
            this.container.appendChild(this.stats.dom);
        }
        // Clock
        // const clock = new THREE.Clock();
        // Animations
        this.animate = () => {
            // this.requestIdleCallback(this.animate);
            this.animateId = window.requestAnimationFrame(this.animate);
            this.render();
        };
        this.animate();
        if (this._onload) {
            this._onload();
        }
        else {
            this._onload = true;
        }
    }
    // 获取/设置onload事件
    get onload() {
        return this._onload;
    }
    set onload(value) {
        if (this._onload) {
            value();
        }
        this._onload = value;
    }
    // 获取/设置元素更新
    get elements() {
        return this._elements;
    }
    set elements(value) {
        // 更新元素
        if (!this.asyncTag) {
            this._elements = value;
            // this._handleElementChange();
            if (this.update) {
                this.update(MapUpdateType.element);
            }
        }
        else {
            this._tempList.push(value);
        }
    }
    // 获取/设置异步标志位
    get asyncTag() {
        return this._asyncTag;
    }
    set asyncTag(value) {
        this._asyncTag = value;
        if (!value) {
            this.elements = this._tempList.pop() || [];
            this._tempList = [];
        }
    }
    // 获取/设置zoom值
    get zoom() {
        return this.camera.position.z;
    }
    set zoom(value) {
        this.camera.position.z = value;
    }
    // 获取/设置background
    get mapBackgroundColor() {
        return this._backgroundColor;
    }
    set mapBackgroundColor(value) {
        this._backgroundColor = value;
        this.scene.background = new THREE.Color(this._backgroundColor);
    }
    dealElementData(collectElements) {
        if (collectElements) {
            this._collectElements = collectElements;
        }
        const collectArr = Array.from(this._collectElements);
        collectArr.sort((a, b) => {
            return (a[1]?.[0]?.weight || 0) - (b[1]?.[0]?.weight || 0);
        });
        const collectMap = new Map(collectArr);
        collectMap.forEach((value, key) => {
            this._handleElementChange(key, value);
        });
        if (this.update) {
            this.update(MapUpdateType.element);
        }
    }
    // 设置是否自动旋转
    setAutoRotate(val) {
        // this.controls.enableRotate = val;
    }
    getMainAngle() {
        return {
            target: this.controls.target,
            camera: this.camera.position,
        };
    }
    resetMainAngle() { }
    // 快照
    getScreenshot() {
        this.render();
        const screenshot = this.renderer.domElement.toDataURL();
        return screenshot;
    }
    // 渲染函数
    render() {
        // Debug
        if (this.debug) {
            this.stats?.update();
        }
        // // update intersects
        // this.intersects?.update(this._group);
        this.intersects?.update(this.scene);
        this.controls?.update();
        // // Render
        this.renderer?.render(this.scene, this.camera);
        this._labelRender?.render(this.scene, this.camera);
        TWEEN?.update();
    }
    // 获取当前位置
    getPosition(event) {
        return this.intersects?.getPosition(event);
    }
    // 根据坐标点返回世界坐标
    getWorldPosition(vertices) {
        return this.intersects.getWorldPosition(vertices);
    }
    _handleElementChange(key, data) {
        const _elementMap = new Map();
        function _DP(elementMap, elements, id, group, that) {
            elements.map((element, index) => {
                const _id = id + element.type + (element.id || '' + index + new Date().getTime()); // 随机值方式优化
                if (elementMap.get(_id)) {
                    const object3D = group.getObjectByName(elementMap.get(_id));
                    if (object3D?.update) {
                        // css2d待考虑
                        object3D.update(element);
                        _elementMap.set(_id, object3D.uuid);
                    }
                    else {
                        const type = element.type;
                        const newObject3D = new Objects[type](element);
                        object3D?.dispose();
                        group.add(newObject3D);
                        _elementMap.set(_id, newObject3D.uuid);
                    }
                    elementMap.delete(_id);
                }
                else {
                    if (_elementMap.get(_id)) {
                        const object3D = group.getObjectByName(_elementMap.get(_id));
                        if (object3D?.update) {
                            // css2d待考虑
                            object3D.update(element);
                            _elementMap.set(_id, object3D.uuid);
                        }
                        else {
                            const type = element.type;
                            const newObject3D = new Objects[type](element);
                            object3D?.dispose();
                            group.add(newObject3D);
                            _elementMap.set(_id, newObject3D.uuid);
                        }
                        console.warn(`type:${element.type} id:${element.id}重复，元素被覆盖`);
                    }
                    else {
                        const type = element.type;
                        const object3D = new Objects[type](element);
                        _elementMap.set(_id, object3D.uuid);
                        group.add(object3D);
                        elementMap.delete(_id);
                    }
                }
                // if (element.children && element.children.length > 0 && element.needDeep) {
                //   _DP(elementMap, element.children, id, group, that);
                // }
            });
        }
        const UpdateDiffOpration = () => {
            if (!this._elementMap.has(key)) {
                this._elementMap.set(key, new Map());
            }
            const elementKeyMap = this._elementMap.get(key);
            _DP(elementKeyMap, data, '', this._group, this);
            // DP(this._elements, '', this._group);
            // const keyElementMap: Map<string, string> = this._elementMap.get(key) as Map<string, string>;
            elementKeyMap?.forEach((uuid) => {
                // 删除旧Map中剩余元素
                this._group.getObjectByName(uuid)?.dispose();
                // element.dispose();
            });
            this._elementMap.set(key, _elementMap);
            // this._elementMap = _elementMap; // 更新Map数据
            // this._elementMap.set({ _eleMap: _elementMap,  }); // 更新Map数据
        };
        this.testTimer(UpdateDiffOpration);
        this.setLog('group中所有元素', this._group.children);
        this.setLog('源数据', this.elements);
        this.setLog('map数据', this._elementMap);
    }
    moveToMesh() { }
    _resize() {
        const height = this.container.clientHeight;
        const width = this.container.clientWidth;
        if (height !== 0) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
            this.renderer?.setSize(width, height);
            this._labelRender.setSize(width, height);
        }
        return this;
    }
    zoomReset() {
        // 还原controls
        this.controls?.reset();
        return this;
    }
    zoomIn(num = 1) {
        const preNum = cameraPositionZToZoom(this.zoom);
        const currentZoom = zoomToCameraPositionZ(preNum + num);
        this.zoom = Math.max(this.controls.minDistance, currentZoom);
        return this;
    }
    zoomOut(num = 1) {
        const preNum = cameraPositionZToZoom(this.zoom);
        if (preNum > 1) {
            let currentZoom = zoomToCameraPositionZ(preNum - num);
            currentZoom = Math.min(this.controls.maxDistance, currentZoom);
            this.zoom = currentZoom;
        }
        else {
            this.zoom = this.controls.maxDistance;
        }
        return this;
    }
    // 坐标转换，dom => canvas
    clientToMapPosition(x, y) {
        const rect = this.container.getBoundingClientRect();
        // Standard x and y in standard coordinate, range from [-1, 1]
        const standardX = ((x - rect.left) / rect.width) * 2 - 1;
        const standardY = -((y - rect.top) / rect.height) * 2 + 1;
        // Get identity vector from camera to target point
        const vec = new THREE.Vector3().set(standardX, standardY, 0.5);
        vec.unproject(this.camera);
        vec.sub(this.camera.position).normalize();
        // Get vector from camera to XY plane
        vec.multiplyScalar(-this.camera.position.z / vec.z);
        // Get intersect on XY plane
        const pos = new THREE.Vector3().copy(this.camera.position).add(vec);
        return { x: pos.x, y: pos.y };
    }
    // 移动到某个点
    moveToPoint({ x, y, z, duration = 500, cb }) {
        if (!this.controls)
            return;
        new TWEEN.Tween({ x1: this.camera.position.x, y1: this.camera.position.y })
            .to({ x1: x, y1: y }, duration)
            .onUpdate((obj) => {
            if (this.controls) {
                this.camera.position.x = obj.x1;
                this.camera.position.y = obj.y1;
                this.controls.target.x = obj.x1;
                this.controls.target.y = obj.y1;
            }
        })
            .onComplete(() => {
            if (this.controls) {
                if (cb && typeof cb === 'function') {
                    cb();
                }
            }
        })
            .start();
    }
    resetElements(elements) {
        const duration = 1000; // 常量
        const property = {
            scale: 1,
        };
        const target = {
            scale: 0.01,
        };
        this.asyncTag = true;
        new TWEEN.Tween(property)
            .to(target, duration)
            .easing(TWEEN.Easing.Sinusoidal.In)
            .onUpdate(() => {
            this._group.scale.set(property.scale, property.scale, 1);
        })
            .onComplete(() => {
            this.zoomReset();
            // this.elements = elements;
            this._tempList.unshift(elements);
            this.asyncTag = false;
            const property = {
                scale: 0.01,
            };
            const target = {
                scale: 1,
            };
            new TWEEN.Tween(property)
                .to(target, duration)
                .easing(TWEEN.Easing.Sinusoidal.In)
                .onUpdate(() => {
                this._group.scale.set(property.scale, property.scale, 1);
            })
                .onComplete(() => {
                if (this.update) {
                    this.update(MapUpdateType.scene);
                }
            })
                .start();
            // this.update();
        })
            .start();
    }
    formatPointsToPlatform(vertices) { }
    // 在debug模式下允许打开log
    setLog(...rest) {
        if (this.debug) {
            console.log(...rest);
        }
    }
    // 在debug模式下允许事件耗时测试
    testTimer(cb) {
        if (this.debug) {
            console.time(`${cb.name}函数测试`);
            cb?.();
            console.timeEnd(`${cb.name}函数测试`);
        }
        else {
            cb?.();
        }
    }
    MapType(type) {
        const maptype = {
            click: 'onClick',
            mousemove: 'onMouseMove',
            mousedown: 'onMouseDown',
            mouseup: 'onMouseUp',
            mouseenter: 'onMouseEnter',
            mouseleave: 'onMouseLeave',
        };
        return maptype[type];
    }
    // 事件处理，拿到具体元素
    EventHandler(event) {
        const type = event.type;
        const target = event.mapTarget; // 当前元素
        const targets = event.mapTargets; // 当前射线穿过的所有元素
        const position = event.mapPosition; // 当前位置
        const worldPosition = event.worldPosition; // 世界坐标
        const onType = this.MapType(type);
        const maptype = onType;
        if (this[maptype] && typeof this[maptype] === 'function') {
            this[maptype]?.(target, targets, position, worldPosition);
        }
        if (target && typeof target[onType] === 'function') {
            target[onType](target, targets, position, worldPosition);
        }
    }
    // 工具group清除
    cleanToolGroup() {
        this._toolGroup.children?.map((obj) => {
            obj?.dispose?.();
        });
        this._toolGroup.clear();
    }
    dispose() {
        // remove requestAnimation
        window.cancelAnimationFrame(this.animateId);
        // remove eventListener
        this.intersects.removeEventListener('mousedown', this.EventHandler);
        this.intersects.removeEventListener('mouseup', this.EventHandler);
        this.intersects.removeEventListener('click', this.EventHandler);
        this.intersects.removeEventListener('mousemove', this.EventHandler);
        this.intersects.removeEventListener('mouseenter', this.EventHandler);
        this.intersects.removeEventListener('mouseleave', this.EventHandler);
        window.removeEventListener('resize', this._resize, false);
        this._group.children?.map((obj) => {
            obj?.dispose?.();
        });
        this._toolGroup.children?.map((obj) => {
            obj?.dispose?.();
        });
        this.scene.traverse((obj) => {
            if (obj instanceof THREE.Mesh) {
                obj.geometry.dispose();
                obj.material.dispose();
            }
            else {
                if (obj.parent) {
                    obj?.parent?.remove(obj);
                }
                obj?.clear();
            }
        });
        this.intersects?.dispose();
        this.controls?.dispose();
        this.renderer?.domElement.remove();
        this._labelRender?.domElement.remove();
        if (this.stats) {
            this.stats?.domElement.remove();
        }
        this.renderer?.forceContextLoss();
        this.renderer?.dispose();
        this.scene?.clear();
        store.clear();
        // remove elements
        this.scene = null;
        this.camera = null;
        this.controls = null;
        this.renderer.domElement = null;
        this._labelRender.domElement = null;
        this._labelRender = null;
        this.renderer = null;
    }
}
export default BaseMap;
